import React, { useState, useEffect } from 'react';
import { Smartphone, Download, CheckCircle, Info } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const PWAStatusIndicator: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('bottom');

  useEffect(() => {
    const checkInstallStatus = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator as any).standalone;
      
      setIsStandalone(standalone || (isIOS && isInStandaloneMode));
      setIsInstalled(standalone || (isIOS && isInStandaloneMode));
    };

    checkInstallStatus();

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
    });

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      }
      
      setDeferredPrompt(null);
    }
  };

  const handleMouseEnter = () => {
    // Check if there's enough space above the button
    const buttonRect = document.querySelector('[data-pwa-button]')?.getBoundingClientRect();
    if (buttonRect) {
      const spaceAbove = buttonRect.top;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      setTooltipPosition(spaceAbove > 100 ? 'top' : 'bottom');
    }
    setShowTooltip(true);
  };

  const getStatusInfo = () => {
    if (isInstalled || isStandalone) {
      return {
        icon: CheckCircle,
        color: 'text-primary',
        tooltip: 'App is installed and running in standalone mode',
        showInstall: false
      };
    } else if (deferredPrompt) {
      return {
        icon: Download,
        color: 'text-blue-500',
        tooltip: 'Click to install the app',
        showInstall: true
      };
    } else {
      return null
    }
  };

  const statusInfo = getStatusInfo();
  const IconComponent = statusInfo?.icon || Info;

  return statusInfo ? (
    <div className="relative">
      <button
        data-pwa-button
        onClick={statusInfo?.showInstall ? handleInstallClick : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
        className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${statusInfo?.color} ${
          statusInfo?.showInstall ? 'cursor-pointer' : 'cursor-default'
        }`}
        title={statusInfo?.tooltip}
      >
        <IconComponent size={20} />
      </button>
      
      {showTooltip && (
        <div className={`absolute left-1/2 transform -translate-x-1/2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg whitespace-nowrap z-50 shadow-lg ${
          tooltipPosition === 'top' 
            ? 'bottom-full mb-2' 
            : 'top-full mt-2'
        }`}>
          {statusInfo?.tooltip}
          <div className={`absolute left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 ${
            tooltipPosition === 'top'
              ? 'top-full border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700'
              : 'bottom-full border-b-4 border-transparent border-b-gray-900 dark:border-b-gray-700'
          }`}></div>
        </div>
      )}
    </div>
  ) : null;
};

export default PWAStatusIndicator;
